import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  BarChart3, 
  Award, 
  Filter,
  RefreshCw
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import SourcingRfpService from '../../services/sourcingRFPService';

// Sub-components
import BidEvaluation from './BidEvaluation';
import AwardManagement from './AwardManagement';

const EvaluationAwardManagement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'evaluation');
  const [rfps, setRfps] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState(id || searchParams.get('rfpId') || '');

  useEffect(() => {
    const loadRfps = async () => {
      try {
        const res = await SourcingRfpService.getRfps();
        const list = res.data?.items || res.items || (Array.isArray(res.data) ? res.data : []);
        setRfps(list);
      } catch (err) {
        console.error('Failed to load RFPs', err);
      }
    };
    loadRfps();
  }, []);

  useEffect(() => {
    if (id && id !== selectedRfpId) {
      setSelectedRfpId(id);
    }
  }, [id, selectedRfpId]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId, rfpId: selectedRfpId });
  };

  const handleRfpChange = (newId) => {
    setSelectedRfpId(newId);
    setSearchParams({ tab: activeTab, rfpId: newId });
    if (newId) {
       navigate(`/sourcing/evaluation-award/${newId}?tab=${activeTab}`);
    } else {
       navigate(`/sourcing/evaluation-award?tab=${activeTab}`);
    }
  };

  const tabs = [
    { id: 'evaluation', label: 'Evaluation Dashboard', icon: <BarChart3 size={18} /> },
    { id: 'award', label: 'Award Decision', icon: <Award size={18} /> },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Evaluation & Award Management"
        description="Unified workspace for technical scoring, commercial comparison, and finalized award decisions."
      />

      <div className="page-container">
        {/* Unified RFP Selector */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3 text-gray-400">
            <Filter size={20} />
            <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">
              Active Sourcing Event:
            </span>
          </div>
          <select
            className="flex-1 bg-gray-50 border border-gray-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            value={selectedRfpId}
            onChange={(e) => handleRfpChange(e.target.value)}
          >
            <option value="">Select an RFP to evaluate...</option>
            {rfps.map((rfp) => (
              <option key={rfp._id} value={rfp._id}>
                {rfp.rfpNumber} - {rfp.title} ({rfp.status})
              </option>
            ))}
          </select>
          <button 
            onClick={() => window.location.reload()}
            className="p-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
            title="Refresh Data"
          >
            <RefreshCw size={18} />
          </button>
        </div>

        {/* Custom Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {tabs.map((tab) => (activeTab === tab.id ? (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className="flex items-center gap-2 px-6 py-3 border-b-2 border-blue-600 text-blue-600 font-semibold transition-all"
            >
              {tab.icon}
              {tab.label}
            </button>
          ) : (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className="flex items-center gap-2 px-6 py-3 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-all font-medium"
            >
              {tab.icon}
              {tab.label}
            </button>
          )))}
        </div>

        {/* Tab Content */}
        <div className="tab-content-wrapper">
          {activeTab === 'evaluation' && (
            <div className="animate-in fade-in duration-300">
              <BidEvaluation key={`eval-${selectedRfpId}`} isNested={true} rfpId={selectedRfpId} />
            </div>
          )}
          {activeTab === 'award' && (
            <div className="animate-in fade-in duration-300">
              <AwardManagement key={`award-${selectedRfpId}`} isNested={true} rfpId={selectedRfpId} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EvaluationAwardManagement;
